/*  Batch-conductor sizing · constants + helpers.
    Extracted from BatchConductor.jsx during the v2.85 refactor to keep
    the main component under ~800 lines and to make the standard NEC /
    IEC option tables + CSV schema easy to unit-test / evolve independently.
*/

export const CIRCUIT_TYPES = [
  { value: "feeder", label: "Feeder / branch" },
  { value: "motor_branch", label: "Motor branch (430.52)" },
  { value: "transformer", label: "Transformer (450.3)" },
  { value: "motor_group", label: "Motor group feeder (430.24)" },
];

export const LOAD_UNITS = ["A", "hp", "kW"];
export const TEMP_RATINGS = [60, 75, 90];
export const MEDIA_OPTIONS = ["conduit", "tray", "free_air"];
export const CONDUIT_MATERIALS = ["", "EMT", "RMC", "PVC-40"];
export const MOTOR_PROTECTIONS = [
  { value: "inverse_time_breaker", label: "Inverse-time breaker" },
  { value: "non_time_delay_fuse", label: "Non-time-delay fuse" },
  { value: "time_delay_fuse", label: "Time-delay (dual-element) fuse" },
  { value: "instantaneous_only_breaker", label: "Instantaneous-only breaker" },
];

export const DEFAULT_ROWS = 5;

export const blankRow = (n = 1) => ({
  tag: `C-${n}`,
  code: "NEC",
  circuit_type: "feeder",
  load_value: 80,
  load_unit: "A",
  load_amps: 80,
  continuous: true,
  continuous_factor: 1.25,
  voltage: 480,
  phases: 3,
  length_ft: 100,
  power_factor: 0.9,
  temp_rating: 75,
  ambient_c: 30,
  parallel_sets: 1,
  cable_type: "THWN-2",
  conductor_material: "copper",
  location_wet: false,
  media: "conduit",
  cables_in_media: 3,
  bundled: true,
  conduit_material: "",
  include_neutral_in_conduit: true,
  allow_next_size_up: false,
  motor_protection: "inverse_time_breaker",
  motor_service_factor: 1.15,
  transformer_kva: 75,
  transformer_secondary_voltage: 208,
  transformer_side: "primary",
  transformer_has_secondary_protection: false,
  // v2.87 · Batch transformer both-sides sizing (mirrors ConductorSizingCard v2.83).
  transformer_supervised: false,
  transformer_primary_length_ft: 50,
  transformer_secondary_length_ft: 30,
  transformer_size_both_sides: false,
  motor_group_motors: [
    { tag: "M-1", hp: 50, voltage: "", phases: "" },
    { tag: "M-2", hp: 25, voltage: "", phases: "" },
  ],
  motor_group_other_loads_amps: 0,
});

/* Columns eligible for bulk-edit (apply-to-selected). Type dictates the
   editor rendered in the bulk toolbar. `options` is required for select
   types. Excluded on purpose: `tag` (should stay unique), and mode-specific
   fields (motor_*, transformer_*, motor_group_*) which are edited in the
   expanded row. */
export const BULK_COLUMNS = [
  { key: "circuit_type", label: "Circuit type", type: "select", options: CIRCUIT_TYPES },
  { key: "load_value", label: "Load (value)", type: "number" },
  { key: "load_unit", label: "Load unit", type: "select",
    options: LOAD_UNITS.map((u) => ({ value: u, label: u })) },
  { key: "continuous_factor", label: "Continuous ×", type: "select",
    options: [{ value: 1.0, label: "1.00" }, { value: 1.25, label: "1.25" }] },
  { key: "voltage", label: "Voltage", type: "number" },
  { key: "phases", label: "Phases", type: "select",
    options: [{ value: 1, label: "1" }, { value: 3, label: "3" }] },
  { key: "length_ft", label: "Length ft", type: "number" },
  { key: "temp_rating", label: "Temp rating", type: "select",
    options: TEMP_RATINGS.map((t) => ({ value: t, label: `${t}°C` })) },
  { key: "ambient_c", label: "Ambient °C", type: "number" },
  { key: "parallel_sets", label: "Parallel sets", type: "number" },
  { key: "cable_type", label: "Cable type", type: "cable" }, // uses cableTypes state
  { key: "conductor_material", label: "Conductor material", type: "select",
    options: [{ value: "copper", label: "Copper (Cu)" }, { value: "aluminum", label: "Aluminum (Al)" }] },
  { key: "media", label: "Media", type: "select",
    options: MEDIA_OPTIONS.map((m) => ({ value: m, label: m })) },
  { key: "conduit_material", label: "Conduit", type: "select",
    options: CONDUIT_MATERIALS.map((c) => ({ value: c, label: c || "(none)" })) },
  { key: "allow_next_size_up", label: "NEC 240.4(B) next-size-up", type: "boolean" },
];

/* CSV import schema. Column order in the template + accepted header names.
   Booleans accept true/false/yes/no/1/0. Missing columns fall back to the
   blankRow defaults. Motor-group / transformer / motor-branch specific
   fields are optionally supported. */
export const CSV_COLUMNS = [
  "tag", "circuit_type", "load_value", "load_unit", "continuous_factor",
  "voltage", "phases", "length_ft", "temp_rating", "ambient_c",
  "parallel_sets", "cable_type", "conductor_material", "media",
  "conduit_material", "allow_next_size_up",
  // optional mode-specific
  "motor_protection", "motor_service_factor",
  "transformer_kva", "transformer_secondary_voltage", "transformer_side",
  "transformer_has_secondary_protection", "motor_group_other_loads_amps",
];

export const CSV_NUMERIC = new Set([
  "load_value", "continuous_factor", "voltage", "phases", "length_ft",
  "temp_rating", "ambient_c", "parallel_sets", "motor_service_factor",
  "transformer_kva", "transformer_secondary_voltage",
  "motor_group_other_loads_amps",
]);
export const CSV_BOOL = new Set([
  "allow_next_size_up", "transformer_has_secondary_protection",
]);

export function parseBool(v) {
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  return s === "true" || s === "yes" || s === "y" || s === "1";
}

/* Tiny RFC-4180-lite CSV parser — handles quoted fields, embedded commas
   and doubled-quote escapes. Good enough for the batch-import UX (users
   pasting spreadsheet exports); we deliberately avoid pulling in papaparse
   just for this one feature. */
export function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        row.push(field); field = "";
        if (row.length > 1 || row[0] !== "") rows.push(row);
        row = [];
      } else field += c;
    }
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}
